import { auth } from "../auth";
import { EmptyState } from "../components/shared";
import { refreshApplication, urlBase64ToUint8Array } from "../lib/appRuntime";
import { money, usPhoneDigits, usPhoneForApi } from "../lib/format";
import { memberApi } from "../memberApi";
import { offlineStore } from "../offlineDb";
import {
  Bell,
  Building2,
  CalendarDays,
  CheckCheck,
  ChevronRight,
  DollarSign,
  Home,
  LogOut,
  MapPin,
  Pencil,
  RefreshCw,
  Trash2,
  Trophy,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

export function ScheduleView({ matches }) {
  if (!matches.length)
    return (
      <EmptyState
        icon={<CalendarDays />}
        title="Your schedule"
        text="No upcoming matches are scheduled for your active teams."
      />
    );
  return (
    <section className="section-stack schedule-view">
      <div className="section-heading schedule-heading">
        <span className="kicker">UPCOMING</span>
        <h1>Your schedule</h1>
      </div>
      {matches.map((match) => {
        const memberTeam = match.teamName || "Your Team";
        const awayTeam = match.isHome ? match.opponentName : memberTeam;
        const homeTeam = match.isHome ? memberTeam : match.opponentName;
        const pubName = match.pubName || "Venue to be announced";
        return (
          <article className="list-card schedule-card" key={match.matchId}>
            <div className="date-tile schedule-date">
              <b>{new Date(`${match.matchDate}T12:00:00`).getDate()}</b>
              <span>
                {new Date(`${match.matchDate}T12:00:00`).toLocaleString("en-US", {
                  month: "short",
                })}
              </span>
            </div>
            <div className="schedule-card-copy">
              <small>
                ROUND {match.roundNumber} · {match.isHome ? "HOME" : "AWAY"}
              </small>
              {match.isBye ? (
                <h2>Bye week</h2>
              ) : (
                <>
                  <h2 className="schedule-phone-matchup">
                    {awayTeam} <em>vs</em> {homeTeam}
                  </h2>
                  <div className="schedule-welcome">
                    <MapPin size={18} />
                    <span>
                      <strong>{pubName}</strong>
                      <small className="schedule-phone-venue">MATCH VENUE</small>
                      <small className="schedule-wide-welcome">
                        WELCOMES <b>{awayTeam}</b> <i>VS</i> <b>{homeTeam}</b>
                      </small>
                    </span>
                  </div>
                </>
              )}
            </div>
          </article>
        );
      })}
    </section>
  );
}

export function SwipeNotification({ item, onOpen, onRemove }) {
  const [offset, setOffset] = useState(0);
  const start = useRef(null);
  const offsetRef = useRef(0);
  const suppressClick = useRef(false);
  const moveTo = (value) => {
    offsetRef.current = value;
    setOffset(value);
  };
  const finishSwipe = () => {
    moveTo(offsetRef.current <= -42 ? -92 : 0);
    start.current = null;
  };
  return (
    <div className={`notification-swipe ${offset < 0 ? "revealed" : ""}`}>
      <button
        className="notification-remove"
        aria-label={`Remove ${item.title}`}
        onClick={() => onRemove(item)}
      >
        <Trash2 />
        <span>Remove</span>
      </button>
      <button
        className={`notification-card ${item.readAt ? "read" : "unread"}`}
        style={{ transform: `translateX(${offset}px)` }}
        onPointerDown={(event) => {
          if (event.pointerType === "mouse" && event.button !== 0) return;
          start.current = {
            x: event.clientX,
            y: event.clientY,
            offset: offsetRef.current,
          };
          suppressClick.current = false;
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!start.current) return;
          const deltaX = event.clientX - start.current.x;
          const deltaY = event.clientY - start.current.y;
          if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
          if (Math.abs(deltaX) > 7) suppressClick.current = true;
          moveTo(Math.max(-92, Math.min(0, start.current.offset + deltaX)));
        }}
        onPointerUp={finishSwipe}
        onPointerCancel={finishSwipe}
        onClick={(event) => {
          if (suppressClick.current) {
            event.preventDefault();
            suppressClick.current = false;
            return;
          }
          if (offsetRef.current < 0) {
            moveTo(0);
            return;
          }
          onOpen(item);
        }}
      >
        <span className="notification-dot" />
        <span>
          <small>
            {item.category} · {new Date(item.createdAt).toLocaleDateString()}
          </small>
          <b>{item.title}</b>
          <p>{item.body}</p>
        </span>
        {item.actionUrl && <ChevronRight />}
      </button>
    </div>
  );
}

export function NotificationsView({ onUnreadChanged }) {
  const [page, setPage] = useState({ unreadCount: 0, items: [] });
  const [preferences, setPreferences] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [notice, setNotice] = useState("Loading your updates…");
  const load = async () => {
    try {
      const [messages, savedPreferences, available] = await Promise.all([
        memberApi.notifications(),
        memberApi.notificationPreferences(),
        memberApi.notificationCapabilities(),
      ]);
      setPage(messages);
      setPreferences(savedPreferences);
      setCapabilities(available);
      setNotice("");
      onUnreadChanged(messages.unreadCount);
    } catch (error) {
      setNotice(error.message);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const open = async (notification) => {
    if (!notification.readAt) {
      await memberApi.markNotificationRead(notification.id);
      setPage((value) => ({
        unreadCount: Math.max(0, value.unreadCount - 1),
        items: value.items.map((item) =>
          item.id === notification.id
            ? { ...item, readAt: new Date().toISOString() }
            : item,
        ),
      }));
      onUnreadChanged(Math.max(0, page.unreadCount - 1));
    }
    if (notification.actionUrl) location.href = notification.actionUrl;
  };
  const markAll = async () => {
    await memberApi.markAllNotificationsRead();
    setPage((value) => ({
      unreadCount: 0,
      items: value.items.map((item) => ({
        ...item,
        readAt: item.readAt || new Date().toISOString(),
      })),
    }));
    onUnreadChanged(0);
  };
  const remove = async (notification) => {
    try {
      await memberApi.removeNotification(notification.id);
      setPage((value) => {
        const removed = value.items.find((item) => item.id === notification.id);
        const unreadCount = Math.max(
          0,
          value.unreadCount - (removed && !removed.readAt ? 1 : 0),
        );
        onUnreadChanged(unreadCount);
        return {
          unreadCount,
          items: value.items.filter((item) => item.id !== notification.id),
        };
      });
    } catch (error) {
      setNotice(error.message);
    }
  };
  const enablePush = async () => {
    try {
      if (!capabilities?.pushAvailable)
        throw new Error(
          "Browser alerts are not enabled on this environment yet.",
        );
      if (!("serviceWorker" in navigator) || !("PushManager" in window))
        throw new Error(
          "This browser does not support app alerts. On iPhone, install this site to the Home Screen first.",
        );
      const registration = await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();
      if (permission !== "granted")
        throw new Error(
          "Notification permission was not granted. You can still see every update in this inbox.",
        );
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          capabilities.vapidPublicKey,
        ),
      });
      const json = subscription.toJSON();
      await memberApi.savePushSubscription({
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        deviceName: navigator.userAgent.includes("iPhone") ? "iPhone" : "Phone",
      });
      const saved = await memberApi.saveNotificationPreferences({
        ...preferences,
        pushEnabled: true,
      });
      setPreferences(saved);
      setNotice("App alerts are on for this device.");
    } catch (error) {
      setNotice(error.message);
    }
  };
  const disablePush = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await memberApi.removePushSubscription(subscription.endpoint);
      await subscription.unsubscribe();
    }
    const saved = await memberApi.saveNotificationPreferences({
      ...preferences,
      pushEnabled: false,
    });
    setPreferences(saved);
    setNotice("App alerts are off. Updates will remain in this inbox.");
  };
  return (
    <section className="section-stack notification-center">
      <div className="section-heading">
        <span className="kicker">LEAGUE UPDATES</span>
        <h1>Notifications</h1>
        <p>
          Invitations, roster changes, schedules, and match activity stay here
          even if your phone is offline.
        </p>
      </div>
      <article className="notification-settings">
        <div>
          <b>App alerts</b>
          <small>
            {preferences?.pushEnabled
              ? "On for this device"
              : "Optional — your inbox always works"}
          </small>
        </div>
        <button
          className="small-button"
          onClick={preferences?.pushEnabled ? disablePush : enablePush}
        >
          {preferences?.pushEnabled ? "Turn off" : "Turn on"}
        </button>
      </article>
      {notice && <p className="notification-notice">{notice}</p>}
      {page.unreadCount > 0 && (
        <button className="mark-all-button" onClick={markAll}>
          <CheckCheck /> Mark all read
        </button>
      )}
      <div className="notification-list">
        {page.items.map((item) => (
          <SwipeNotification
            key={item.id}
            item={item}
            onOpen={open}
            onRemove={remove}
          />
        ))}
      </div>
      {!notice && page.items.length === 0 && (
        <EmptyState
          icon={<Bell />}
          title="You're all caught up"
          text="New league and team updates will appear here."
        />
      )}
    </section>
  );
}

export function ProfileView({ dashboard, onSaved, onSwitch }) {
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({
    nickname: "",
    emailOptIn: false,
    smsOptIn: false,
    preferredCommunicationChannel: "None",
    ...dashboard.profile,
    phone: usPhoneDigits(dashboard.profile.phone),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [payingFeeId, setPayingFeeId] = useState("");
  const paymentResult = new URLSearchParams(window.location.search).get("payment");
  useEffect(() => {
    let active = true;
    memberApi.paymentStatus()
      .then((value) => active && setPaymentStatus(value))
      .catch(() => active && setPaymentStatus({ canPayOnline: false, organizationStatus: "Unavailable" }));
    return () => { active = false; };
  }, []);
  const stats = dashboard.statistics;
  const due = dashboard.fees.reduce((sum, fee) => sum + fee.balance, 0);
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const saved = await onSaved({
        ...profile,
        phone: usPhoneForApi(profile.phone),
        smsOptIn: false,
        preferredCommunicationChannel: profile.emailOptIn ? "Email" : "None",
      });
      setProfile((value) => ({
        ...value,
        ...saved,
        phone: usPhoneDigits(saved?.phone ?? value.phone),
      }));
      setEditing(false);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };
  const payFee = async (fee) => {
    setPayingFeeId(fee.id);
    setError("");
    try {
      const checkout = await memberApi.createFeeCheckout(fee.id);
      window.location.assign(checkout.url);
    } catch (paymentError) {
      setError(paymentError.message);
      setPayingFeeId("");
    }
  };
  return (
    <section className="section-stack profile-view">
      {paymentResult === "success" && (
        <div className="payment-notice payment-success" role="status">
          <b>Payment submitted</b>
          <span>Stripe is confirming your payment. Your balance updates only after secure confirmation.</span>
        </div>
      )}
      {paymentResult === "cancelled" && (
        <div className="payment-notice" role="status">
          <b>Payment cancelled</b><span>No charge was recorded. You can return whenever you are ready.</span>
        </div>
      )}
      <div className="profile-hero">
        <div className="avatar">
          {profile.firstName?.[0]}
          {profile.lastName?.[0]}
        </div>
        <span className="kicker">{profile.membershipDisplayId}</span>
        <h1>
          {profile.firstName} {profile.lastName}
        </h1>
        {profile.nickname && (
          <span className="profile-nickname">“{profile.nickname}”</span>
        )}
        <p>{profile.status} member</p>
        <button className="small-button" onClick={() => setEditing(!editing)}>
          <Pencil size={14} /> Edit profile
        </button>
      </div>
      {editing && (
        <form className="profile-form" onSubmit={save}>
          <label>
            First name
            <input
              required
              value={profile.firstName}
              onChange={(event) =>
                setProfile({ ...profile, firstName: event.target.value })
              }
            />
          </label>
          <label>
            Last name
            <input
              required
              value={profile.lastName}
              onChange={(event) =>
                setProfile({ ...profile, lastName: event.target.value })
              }
            />
          </label>
          <label className="profile-nickname-field">
            Nickname or alias (optional)
            <input
              maxLength="100"
              value={profile.nickname || ""}
              onChange={(event) =>
                setProfile({ ...profile, nickname: event.target.value })
              }
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={profile.email || ""}
              onChange={(event) =>
                setProfile({ ...profile, email: event.target.value })
              }
            />
          </label>
          <label>
            Phone
            <div className="us-phone-input">
              <span>+1</span>
              <input
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength="10"
                placeholder="8175551212"
                value={usPhoneDigits(profile.phone)}
                onChange={(event) =>
                  setProfile({
                    ...profile,
                    phone: event.target.value.replace(/\D/g, "").slice(0, 10),
                  })
                }
              />
            </div>
          </label>
          <fieldset className="claim-preferences">
            <legend>Communication choices</legend>
            <label className="claim-consent">
              <input
                type="checkbox"
                checked={profile.emailOptIn}
                onChange={(event) =>
                  setProfile({
                    ...profile,
                    emailOptIn: event.target.checked,
                    smsOptIn: false,
                    preferredCommunicationChannel: event.target.checked
                      ? "Email"
                      : "None",
                  })
                }
              />
              <span>
                <b>Email updates</b>
                <small>
                  Receive important league, roster, schedule, fee, and
                  match-related email when an app alert is unavailable.
                </small>
              </span>
            </label>
            <p className="claim-legal">
              Browser alerts are managed from Updates. Your in-app inbox remains
              available without opting in.
            </p>
          </fieldset>
          {error && <p className="form-error">{error}</p>}
          <button className="action-primary" disabled={saving}>
            {saving ? "Saving…" : "Save profile & preferences"}
          </button>
        </form>
      )}
      <div className="stat-grid">
        <div>
          <b>{stats.gamesPlayed}</b>
          <span>Games</span>
        </div>
        <div>
          <b>{stats.wins}</b>
          <span>Wins</span>
        </div>
        <div>
          <b>{stats.winPercentage}%</b>
          <span>Win rate</span>
        </div>
        <div>
          <b>{stats.bestOut || "—"}</b>
          <span>Best out</span>
        </div>
      </div>
      {!!stats.recognizedStats?.length && (
        <article className="detail-card recognized-stats">
          <header>
            <Trophy />
            <h2>Season achievements</h2>
          </header>
          {stats.recognizedStats.map((stat) => (
            <div
              className="detail-row"
              key={`${stat.seasonId}-${stat.gameStatPolicyId}`}
            >
              <span>
                <b>{stat.name}</b>
                <small>Best qualifying turn: {stat.bestValue}</small>
              </span>
              <strong>
                {stat.total} {stat.shortLabel}
              </strong>
            </div>
          ))}
        </article>
      )}
      <article className="detail-card">
        <header>
          <DollarSign />
          <h2>Fees</h2>
          <strong className={due > 0 ? "due" : "paid"}>
            {due > 0 ? `${money(due)} due` : "Paid up"}
          </strong>
        </header>
        {dashboard.fees.length ? (
          dashboard.fees.map((fee) => (
            <div className="detail-row fee-row" key={fee.id}>
              <span>
                <b>{fee.description}</b>
                <small>{fee.coverageLabel}</small>
              </span>
              <span className="fee-actions">
                <strong>{fee.balance ? money(fee.balance) : "Paid"}</strong>
                {fee.balance > 0 && paymentStatus?.canPayOnline && (
                  <button
                    className="pay-fee-button"
                    disabled={payingFeeId === fee.id}
                    onClick={() => payFee(fee)}
                  >
                    {payingFeeId === fee.id ? "Opening…" : "Pay securely"}
                  </button>
                )}
              </span>
            </div>
          ))
        ) : (
          <p className="muted-copy">No fees have been assessed.</p>
        )}
        {due > 0 && paymentStatus && !paymentStatus.canPayOnline && (
          <p className="payment-unavailable">Online payment is not available for this organization yet. Your league organizer can still record an offline payment.</p>
        )}
        {error && <p className="form-error">{error}</p>}
      </article>
      <article className="detail-card">
        <header>
          <Users />
          <h2>Your teams</h2>
        </header>
        {dashboard.teams.map((team) => (
          <div className="detail-row" key={team.registrationId}>
            <span>
              <b>{team.teamName}</b>
              <small>
                {team.seasonName} · {team.nightName}
              </small>
            </span>
            {team.isCaptain && <strong>Captain</strong>}
          </div>
        ))}
      </article>
      <div className="account-actions profile-account-actions">
        <button onClick={refreshApplication}>
          <RefreshCw /> Refresh app
        </button>
        <button onClick={onSwitch}>
          <Building2 /> Switch organization
        </button>
        <button
          onClick={async () => {
            await offlineStore.clearForSignOut();
            await auth.signOut();
          }}
        >
          <LogOut /> Sign out
        </button>
      </div>
    </section>
  );
}
